'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  parseAppSetting,
  type AppSettingKey,
  type AppSettings,
} from '../electron-shared/app-settings/app-settings-schema';

export type SharedAppSettingStatus = 'loading' | 'ready' | 'unavailable';

type SharedAppSettingState<K extends AppSettingKey> = {
  status: SharedAppSettingStatus;
  value: AppSettings[K] | null;
};

export type SharedAppSetting<K extends AppSettingKey> =
  SharedAppSettingState<K> & {
    save: (value: AppSettings[K]) => Promise<void>;
  };

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

export function useSharedAppSetting<K extends AppSettingKey>(
  key: K,
): SharedAppSetting<K> {
  const [state, setState] = useState<SharedAppSettingState<K>>({
    status: 'loading',
    value: null,
  });

  useEffect(() => {
    const settingsApi = window.electronAPI?.settings;

    if (!settingsApi) {
      setState({ status: 'unavailable', value: null });

      return;
    }

    let cancelled = false;

    const unsubscribe = settingsApi.onChanged((change) => {
      if (change.key !== key) {
        return;
      }

      const value = parseAppSetting({ key, value: change.value });

      if (value !== null) {
        setState({ status: 'ready', value });
      }
    });

    settingsApi
      .get(key)
      .then((value) => {
        if (!cancelled) {
          setState((current) =>
            current.status === 'ready' ? current : { status: 'ready', value },
          );
        }
      })
      .catch((error: unknown) => {
        console.warn(
          `[useSharedAppSetting] Failed to read shared setting "${key}"; continuing with this window's value`,
          { error: toError(error) },
        );

        if (!cancelled) {
          setState({ status: 'unavailable', value: null });
        }
      });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [key]);

  const save = useCallback(
    async (value: AppSettings[K]): Promise<void> => {
      const settingsApi = window.electronAPI?.settings;

      if (!settingsApi) {
        return;
      }

      setState({ status: 'ready', value });

      try {
        await settingsApi.set(key, value);
      } catch (error: unknown) {
        console.warn(
          `[useSharedAppSetting] Failed to save shared setting "${key}"; other windows keep their current value`,
          { error: toError(error) },
        );
      }
    },
    [key],
  );

  return { ...state, save };
}
